<template>
  <div class="agila-dropdown-filter">
    <div>
      <label
        :id="props.id + '-label'"
        :for="props.id"
        v-if="props.label && props.showLabel"
        >{{ props.label }}</label
      >
      <agila-form-error
        :error="error"
        v-if="props.showError"
        :id="props.id + '-error'"
      ></agila-form-error>
      <Select
        v-model="inputValue"
        ref="dropdown"
        filter
        autoFilterFocus
        :tabindex="0"
        :dataKey="props.dataKey"
        :inputId="props.dataKey"
        :optionLabel="props.optionLabel"
        :placeholder="placeholder"
        :emptyFilterMessage="props.emptyFilterMessage"
        :options="options"
        :resetFilterOnHide="true"
        @change="
          readyForCheck = true;
          updateValue();
        "
        @input="updateValue()"
        @filter="filterDropdown($event)"
        :scrollHeight="props.scrollHeight"
        :aria-label="props.label + ':' + placeholder"
        :invalid="!!error"
        :pt="{
          header: { class: 'agila-dropdown-filter-header' },
          overlay: {
            class: 'agila-dropdown-filter-panel',
          },
        }"
        :aria-describedby="props.id + '-error'"
      >
        <template #header>
          <div class="agila-dropdown-filter-headline" v-if="props.label">
            {{ props.label }}
          </div>
          <div class="agila-icon-skip-container" @click="dropdown.hide()">
            <div class="agila-icon-skip"></div>
          </div>
        </template>
      </Select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineEmits, defineProps, ref, withDefaults, defineExpose } from "vue";
import Select from "primevue/select";
import AgilaFormError from "./agilaFormError.vue";
import useSharedFormLogic from "../helper/functions/useSharedFormLogic";

interface Props {
  emptyFilterMessage?: string;
  dataKey?: string;
  optionLabel?: string;
  id: string;
  modelValue?: any;
  placeholder?: string;
  scrollHeight?: string;
  label?: string;
  showLabel?: boolean;
  showError?: boolean;
  options: Array<any>;
  rules?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  emptyFilterMessage: "Keine Ergebnisse gefunden",
  dataKey: "id",
  optionLabel: "name",
  id: "",
  modelValue: null,
  placeholder: "Bitte wählen",
  scrollHeight: "394px",
  label: "",
  showLabel: false,
  showError: false,
  options: () => [],
  rules: () => [],
});

const dropdown = ref(null);
const filterValue = ref();
const placeholder = ref(props.placeholder);

const emit = defineEmits<{
  (e: "update:modelValue", modelValue: string): void;
  (e: "update:error", error: string): void;
  (e: "selectItem", modelValue: any): void;
}>();

function filterDropdown(event: any) {
  filterValue.value = event.value;
  if (inputValue.value) {
    placeholder.value = inputValue.value[props.optionLabel];
  }
}

const { readyForCheck, inputValue, error, updateValue, validate } =
  useSharedFormLogic(props, emit);

defineExpose({
  validate: () => {
    readyForCheck.value = true;
    return validate(inputValue.value);
  },
});
</script>

<style lang="scss">
.agila-dropdown-filter {
  margin-bottom: 20px;

  label {
    font-family: var(--font-open-sans-regular), sans-serif;
    color: var(--color-gray-700);
  }

  .p-select {
    width: 100%;
    box-sizing: border-box;
    padding: 11px;
    margin-top: 8px;

    &:not(.p-disabled).p-focus {
      box-shadow: 0 0 0 1px var(--color-white), 0 0 0 3px var(--color-blue-600);
    }
    .p-select-label {
      line-height: 24px;
    }
    &:hover:not(:focus) {
      border: 2px solid var(--color-blue-600);
      padding: 10px;
    }
    &.p-invalid {
      border: 2px solid var(--color-red-error) !important;
      padding: 10px;
    }
  }
}

.agila-dropdown-filter-panel {
  z-index: 1000001 !important;

  .p-select-option {
    line-height: 24px;
  }
  @media screen and (max-width: 544px) {
    .p-select-list-container {
      max-height: calc(100vh - 103px) !important;
    }
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100%;
    height: 100%;
    display: block !important;
    z-index: 10000000 !important;
    visibility: unset !important;

    .agila-icon-skip-container {
      top: 4px;
      right: 12px;
      position: absolute;
      cursor: pointer;
      height: 16px;
      padding: 10px;
      box-sizing: initial;
    }

    .agila-dropdown-filter-headline {
      padding: 12px 20px 0;
      font-family: var(--font-roboto-medium), sans-serif;
      color: var(--color-gray-600);
    }
  }
  @media screen and (min-width: 545px) {
    .agila-icon-skip,
    .agila-dropdown-filter-headline {
      display: none !important;
    }
  }
}
.agila-dropdown-filter-panel {
  .agila-dropdown-filter-header,
  .agila-dropdown-filter-headline {
    background: var(--color-blue-50);
  }

  .p-inputtext {
    box-sizing: border-box;
    border-color: var(--color-gray-600);
    width: 100%;
    padding: 11px;
    font-family: var(--font-open-sans-regular) sans-serif;
    font-size: 16px;
    line-height: 24px;

    &:focus {
      box-shadow: 0 0 0 1px var(--color-white), 0 0 0 3px var(--color-blue-600);
    }
    &:hover:not(:focus) {
      border: 2px solid var(--color-blue-600);
      padding: 10px;
    }
  }
}
</style>
